import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import axios from "axios";

import Api from "../api/Api";
import { BASE_API_URL } from "../api/const";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  IconButton,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from "@mui/material";



import HomeIcon from "@mui/icons-material/Home";
import filmStore from "../stores/moviesStore";
import { observer } from "mobx-react-lite";
import Header from "../pages/Header";
import moviesStore from "./../stores/moviesStore";
import RateCanvas from "../components/RateCanvas";
import {
  PODBORKI_API_TOKEN,
  PODBORKI_AVAILABLE_API_URL,
  getFilmPodborkaChangeApiUrl,
} from "../api/podborki";

export interface OptionItem {
  id: number;
  name: string;
}

export interface ApiResponsePortalGenre {
  status: number;
  success: boolean;
  data: {
    genre: OptionItem[];
    country: OptionItem[];
    year: OptionItem[];
    type_content: OptionItem[];
    category: OptionItem[];
    sort: OptionItem[];
    distributor: OptionItem[];
  };
}

interface PodborkaListItem {
  title: string;
  cid: number;
  type: number;
}

const FilmData: React.FC = observer(() => {
  const location = useLocation();
  const [filmId, setFilmId] = useState<number | null>(null);
  const [genres, setGenres] = useState<OptionItem[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [podborki, setPodborki] = useState<OptionItem[]>([]);
  const [selectedPodborki, setSelectedPodborki] = useState<number[]>([]);
  const [podborkiLoading, setPodborkiLoading] = useState(false);
  const [podborkiError, setPodborkiError] = useState<string | null>(null);
  const [pendingPodborki, setPendingPodborki] = useState<number[]>([]);

  // Вытаскиваем id из URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    if (id) {
      setFilmId(Number(id));
    }
  }, [location]);

  useEffect(() => {
    if (filmId !== null) {
      moviesStore.fetchMovieById(filmId);
    }
  }, [filmId]);

  // Загружаем жанры с портала через axios
  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const res = await axios.get<ApiResponsePortalGenre>(
          `https://iptvn.idc.md/api/v3/portal/listFilter`
        );
        if (res.data.success) {
          setGenres(res.data.data.genre);
        }
      } catch (error) {
        console.error("Ошибка загрузки жанров:", error);
      }
    };

    fetchGenres();
  }, []);

  useEffect(() => {
    const fetchPodborki = async () => {
      setPodborkiLoading(true);
      setPodborkiError(null);

      try {
        const response = await axios.get<PodborkaListItem[]>(
          PODBORKI_AVAILABLE_API_URL,
          {
            headers: {
              "Http-X-Token": PODBORKI_API_TOKEN,
            },
          }
        );

        if (!Array.isArray(response.data)) {
          throw new Error("Сервис вернул некорректный список подборок");
        }

        const items = response.data
          .filter((item) => item.type === 3)
          .map((item) => ({ id: item.cid, name: item.title }));

        setPodborki([...items].sort((a, b) => a.name.localeCompare(b.name, "ru")));
      } catch (error) {
        const message = axios.isAxiosError(error)
          ? error.response?.data?.message || error.message
          : error instanceof Error
            ? error.message
            : "Неизвестная ошибка";
        setPodborkiError(`Не удалось загрузить подборки: ${message}`);
      } finally {
        setPodborkiLoading(false);
      }
    };

    fetchPodborki();
  }, []);

  // при загрузке фильма ставим галочки на его жанрах
  useEffect(() => {
    if (moviesStore.movie?.genre) {
      const movieGenreIds = moviesStore.movie.genre.map((g) => g.id);
      setSelectedGenres(movieGenreIds);
    }
  }, [moviesStore.movie]);

  useEffect(() => {
    if (moviesStore.movie?.genreExt) {
      setSelectedPodborki(moviesStore.movie.genreExt.map((item) => item.id));
    }
  }, [moviesStore.movie]);

const handleGenreChange = async (id: number) => {
  // Вычисляем, будет ли жанр выбран или снят
  const isSelected = selectedGenres.includes(id);

  // Обновляем локальное состояние
  setSelectedGenres((prev) =>
    isSelected ? prev.filter((g) => g !== id) : [...prev, id]
  );

  // Отправляем запрос на сервер
  try {
    await Api.post(`${BASE_API_URL}/movies/changeStatusGenre`, {
      movie_id: moviesStore.movie?.id,
      cid: id,
      status: isSelected ? 0 : 1, // если был выбран — снимаем (0), иначе выбираем (1)
    });

  } catch (error) {
    console.error("Ошибка при изменении жанра", error);
    // В случае ошибки можно откатить состояние
    setSelectedGenres((prev) =>
      isSelected ? [...prev, id] : prev.filter((g) => g !== id)
    );
  }
};

  const handlePodborkaChange = async (podborkaId: number) => {
    if (filmId === null || pendingPodborki.includes(podborkaId)) return;

    const isSelected = selectedPodborki.includes(podborkaId);
    const nextStatus: 0 | 1 = isSelected ? 0 : 1;

    setSelectedPodborki((previous) =>
      isSelected
        ? previous.filter((id) => id !== podborkaId)
        : [...previous, podborkaId]
    );
    setPendingPodborki((previous) => [...previous, podborkaId]);
    setPodborkiError(null);

    try {
      const response = await axios.get(
        getFilmPodborkaChangeApiUrl(filmId, podborkaId, nextStatus),
        {
          headers: {
            "Http-X-Token": PODBORKI_API_TOKEN,
          },
        }
      );

      if (response.data?.success === false) {
        throw new Error(response.data?.message || "Сервис не подтвердил изменение");
      }
    } catch (error) {
      setSelectedPodborki((previous) =>
        isSelected
          ? [...previous, podborkaId]
          : previous.filter((id) => id !== podborkaId)
      );

      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : "Неизвестная ошибка";
      setPodborkiError(`Не удалось изменить подборку: ${message}`);
    } finally {
      setPendingPodborki((previous) => previous.filter((id) => id !== podborkaId));
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      {/* Тут надо разместить компонент Header */}
      <Header />

      {/* Заголовок с голубым фоном и закругленными углами */}
      <Paper
        sx={{
          backgroundColor: "#E0F7FA", // Светло-голубой цвет
          padding: 2,
          borderRadius: 2,
          marginBottom: 2, // Отступ снизу
          display: "flex", // Для выравнивания иконки и заголовка в одну линию
          alignItems: "center",
        }}
      >
        {/* Заголовок по центру */}
        <Typography
          variant="h5"
          align="center"
          color="primary"
          sx={{ flexGrow: 1 }}
        >
          {moviesStore.movie?.title ?? "Имя не получено"}
        </Typography>
      </Paper>

      {/* Контейнер для трёх колонок */}
      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Левая колонка — 25% */}
        <Box
          sx={{
            flex: "0 0 25%",
            backgroundColor: "#F5F5F5",
            padding: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 1,
          }}
        >
          {moviesStore.movie?.logo ? (
            <img
              src={moviesStore.movie?.logo}
              alt="Постер фильма"
              width="280"
              height="420"
              style={{ borderRadius: "8px" }}
            />
          ) : (
            <Typography>Загрузка изображения...</Typography>
          )}

          <Box sx={{ paddingTop: 2 }}>
            <RateCanvas
              rates={{
                vote_kp: moviesStore.movie?.vote_kp,
                rate_kp: moviesStore.movie?.rate_kp,
                age: moviesStore.movie?.rate_age,
                vote_imdb: moviesStore.movie?.vote_imdb,
                rate_imdb: moviesStore.movie?.rate_imdb,
              }}
            />
          </Box>
          <Box>
            {moviesStore.movie?.kp_id ? (
              <a
                href={`https://www.kinopoisk.ru/film/${moviesStore.movie.kp_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                кинопоиск
              </a>
            ) : null}
          </Box>
        </Box>

        {/* Центральная колонка — 50% */}
        <Box sx={{ flex: "1 1 50%", display: "flex", flexDirection: "column" }}>
          {/* Блок "Жанры" */}
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 1,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography
              variant="subtitle2"
              align="center"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: "primary.main",
                fontSize: "0.85rem",
              }}
            >
              Жанры
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0,
              }}
            >
              {genres.map((genre) => (
                <Box
                  key={genre.id}
                  sx={{
                    width: "calc(33.333% - 8px)", // 3 колонки
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedGenres.includes(genre.id)}
                        onChange={() => handleGenreChange(genre.id)}
                      />
                    }
                    label={genre.name}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.8rem",
                        lineHeight: 1.1,
                      },
                      "& .MuiCheckbox-root": {
                        padding: "2px",
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Блок "Подборки" */}
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography
              variant="subtitle2"
              align="center"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: "primary.main",
                fontSize: "0.85rem",
              }}
            >
              Подборки
            </Typography>

            {podborkiError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {podborkiError}
              </Alert>
            )}

            {podborkiLoading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 0.25,
                }}
              >
                {podborki.map((podborka) => (
                  <FormControlLabel
                    key={podborka.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedPodborki.includes(podborka.id)}
                        disabled={pendingPodborki.includes(podborka.id)}
                        onChange={() => handlePodborkaChange(podborka.id)}
                      />
                    }
                    label={podborka.name}
                    sx={{
                      m: 0,
                      minWidth: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.8rem",
                        lineHeight: 1.2,
                      },
                      "& .MuiCheckbox-root": {
                        p: "3px",
                        mr: 0.5,
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Правая колонка — 25% */}
        <Box
          sx={{
            flex: "0 0 18%",
            backgroundColor: "#F5F5F5",
            padding: 2,
            borderRadius: 1,
          }}
        ></Box>
      </Box>
    </Box>
  );
});

export default FilmData;
